import { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Shield, Cookie, Eye, Code2, Save } from "lucide-react";

const DOC_TABS = [
  { key: "terms", label: "Terms of Service", icon: FileText },
  { key: "privacy", label: "Privacy Policy", icon: Shield },
  { key: "cookies", label: "Cookie Policy", icon: Cookie },
];

const REMARK_PLUGINS = [remarkGfm];

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-sm border text-sm font-semibold tracking-tight transition-colors duration-150 active:scale-[0.98] ${
        active
          ? "bg-zinc-950 text-white border-zinc-950"
          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950 hover:text-zinc-950"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

export default function LegalEditor() {
  const [active, setActive] = useState("terms");
  const [docs, setDocs] = useState({}); // type -> { content, last_updated }
  const [drafts, setDrafts] = useState({}); // type -> current textarea value
  const [loadingType, setLoadingType] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDoc = useCallback(async (docType) => {
    setLoadingType(docType);
    try {
      const { data } = await api.get(`/legal/${docType}`);
      setDocs((s) => ({ ...s, [docType]: data }));
      setDrafts((s) => (s[docType] === undefined ? { ...s, [docType]: data.content } : s));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingType(null);
    }
  }, []);

  useEffect(() => {
    if (!docs[active]) loadDoc(active);
  }, [active, docs, loadDoc]);

  const draft = drafts[active] ?? "";
  const dirty = docs[active] && draft !== docs[active].content;

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/legal/${active}`, { content: draft });
      toast.success(`${DOC_TABS.find((t) => t.key === active).label} saved`);
      setDocs((s) => ({ ...s, [active]: { ...s[active], content: draft, last_updated: data.last_updated } }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onRevert = () => {
    setDrafts((s) => ({ ...s, [active]: docs[active]?.content ?? "" }));
  };

  const lastUpdated = docs[active]?.last_updated
    ? new Date(docs[active].last_updated).toLocaleString("en-GB")
    : "—";

  return (
    <section className="mt-16" data-testid="admin-legal-editor">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
            <FileText className="h-5 w-5" /> Legal pages
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Edit the Terms, Privacy and Cookie pages in Markdown. Saves go live instantly — no redeploy needed.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">Last saved · {lastUpdated}</div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {DOC_TABS.map((t) => (
          <TabButton
            key={t.key}
            active={active === t.key}
            onClick={() => setActive(t.key)}
            icon={t.icon}
            label={t.label}
            testId={`legal-tab-${t.key}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="border border-zinc-200 rounded-sm">
          <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
            <Code2 className="h-4 w-4 text-zinc-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Markdown source</span>
          </div>
          <Label htmlFor={`legal-editor-${active}`} className="sr-only">Markdown</Label>
          <textarea
            id={`legal-editor-${active}`}
            data-testid={`legal-editor-${active}`}
            value={draft}
            onChange={(e) => setDrafts((s) => ({ ...s, [active]: e.target.value }))}
            spellCheck="true"
            className="w-full min-h-[600px] p-4 font-mono text-sm leading-relaxed text-zinc-900 bg-white border-0 outline-none focus:ring-0 resize-y"
            placeholder={loadingType === active ? "Loading…" : ""}
          />
        </div>

        {/* Live preview */}
        <div className="border border-zinc-200 rounded-sm">
          <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
            <Eye className="h-4 w-4 text-zinc-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Live preview</span>
          </div>
          <div
            data-testid={`legal-preview-${active}`}
            className="p-6 min-h-[600px] overflow-y-auto text-zinc-700 leading-relaxed text-[15px] space-y-5
                       [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-zinc-950 [&_h2]:mt-8 [&_h2]:mb-2
                       [&_h3]:font-heading [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-zinc-950 [&_h3]:mt-6 [&_h3]:mb-2
                       [&_p]:my-3
                       [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul]:space-y-1.5
                       [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_ol]:space-y-1.5
                       [&_li]:marker:text-zinc-400
                       [&_a]:text-[#0038FF] [&_a]:underline [&_a]:underline-offset-2
                       [&_code]:bg-zinc-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded-sm [&_code]:text-[13px] [&_code]:text-zinc-900
                       [&_strong]:text-zinc-950"
          >
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{draft || ""}</ReactMarkdown>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-zinc-500">
          Markdown cheatsheet: <code className="text-zinc-700">## Heading</code> · <code className="text-zinc-700">**bold**</code> · <code className="text-zinc-700">- bullet</code> · <code className="text-zinc-700">[link](https://...)</code>
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onRevert}
            disabled={!dirty || saving}
            data-testid={`legal-revert-${active}`}
            className="h-11 rounded-sm border-zinc-300"
          >
            Revert
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={!dirty || saving}
            data-testid={`legal-save-${active}`}
            className="h-11 px-5 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold tracking-tight active:scale-[0.98] disabled:opacity-60"
          >
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>
    </section>
  );
}
